import { watch } from 'vue'
import { storeToRefs } from 'pinia'
import type { RootData } from '~/definitions/AllDefinitions'
import { useUserStore } from '~/store/UserStore'
import { useMainStore } from '~/store/MainStore'

// Safe as module-level state because this is a .client.ts plugin —
// it only runs in the browser (one user per runtime). If this file
// is ever renamed without the .client suffix, this variable would be
// shared across SSR requests. Do not remove the .client suffix.
let locationWatcherRegistered = false

export default defineNuxtPlugin({
  name: 'posthog-identify',
  hooks: {
    'rootData:ready'(rootData: RootData) {
      const { $posthog } = useNuxtApp()
      const mainStore = useMainStore()

      // Register tenant context as super properties.
      // PostHog attaches these to EVERY event automatically —
      // pageviews, errors, autocapture, and custom captures.
      //
      // NOTE: rootData:ready fires on EVERY navigation (see
      // middleware/getRootData.ts line 42). Calling register()
      // multiple times is safe — it overwrites existing values.
      $posthog.register({
        tenant: window.location.hostname.split('.')[0],
        restaurant_name: rootData.name,
        restaurant_uuid: rootData.uuid,
        ...(mainStore.currentLocation && {
          location_uuid: mainStore.currentLocation.uuid,
          location_name: mainStore.currentLocation.name,
        }),
      })

      // Watch for location changes — BUT only register the watcher
      // once. rootData:ready fires on every navigation, and each
      // watch() call adds a NEW watcher. Without this guard, we'd
      // accumulate duplicate watchers that all fire on location change.
      if (!locationWatcherRegistered) {
        locationWatcherRegistered = true
        const { currentLocation } = storeToRefs(mainStore)
        watch(currentLocation, (newLocation) => {
          if (newLocation) {
            $posthog.register({
              location_uuid: newLocation.uuid,
              location_name: newLocation.name,
            })
          }
        })
      }

      // Identify user if already logged in (returning user with persisted session)
      const userStore = useUserStore()
      if (userStore.loggedIn && userStore.user?.uuid) {
        $posthog.identify(userStore.user.uuid, {
          name: userStore.user.firstName ?? undefined,
          email: userStore.user.email ?? undefined,
        })
      }
    },
  },
})
